import { useState, useEffect, useRef } from "react";
import { useAutocompleteSuggestions } from "hooks/usePlaces";
import { AutoComplete } from "primereact/autocomplete";

// Interface for the selected item
interface PlacePredictionItem {
  placeId: string;
  description: string;
}

// Interface with geographic coordinates
interface PlaceWithGeoData extends PlacePredictionItem {
  latitude?: number;
  longitude?: number;
  formattedAddress?: string;
}

interface PlacesSearchBoxProps {
  onPlaceSelect?: (place: PlaceWithGeoData | null) => void;
  placeholder?: string;
  className?: string;
  debounceTime?: number;
  clearOnSelect?: boolean;
}

export const PlacesSearchBox = ({
  onPlaceSelect,
  placeholder = "Search address...",
  className = "",
  debounceTime = 300,
  clearOnSelect = true,
}: PlacesSearchBoxProps) => {
  const [input, setInput] = useState("");
  const [debouncedInput, setDebouncedInput] = useState("");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasSearchInput = input.trim().length > 0;

  const { suggestions, isLoading, getPlaceDetails } =
    useAutocompleteSuggestions(debouncedInput);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Only update if there's a meaningful change and input has at least 2 characters
      const trimmedInput = input.trim();
      if (trimmedInput !== debouncedInput && trimmedInput.length >= 2) {
        setDebouncedInput(trimmedInput);
      } else if (trimmedInput.length < 2 && debouncedInput !== "") {
        setDebouncedInput("");
      }
    }, debounceTime);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [input, debounceTime, debouncedInput]);

  const autoCompleteOptions: PlacePredictionItem[] = suggestions.map(
    ({ placePrediction }) => ({
      placeId: placePrediction?.placeId || "",
      description: placePrediction?.text.text || "",
    })
  );

  const handlePlaceSelect = async (place: PlacePredictionItem | null) => {
    // When selecting an address, clear the input if clearOnSelect is true
    if (clearOnSelect) {
      setInput("");
      setDebouncedInput("");
    }

    if (place && onPlaceSelect) {
      try {
        setIsLoadingDetails(true);

        // Get place details including latitude and longitude
        const placeDetails = await getPlaceDetails(place.placeId);

        if (placeDetails?.geometry?.location) {
          const placeWithGeo: PlaceWithGeoData = {
            ...place,
            latitude: placeDetails.geometry.location.lat(),
            longitude: placeDetails.geometry.location.lng(),
            formattedAddress:
              placeDetails.formatted_address || place.description,
          };

          onPlaceSelect(placeWithGeo);
        } else {
          onPlaceSelect(place);
        }
      } catch (error) {
        console.error("Error getting place details:", error);
        onPlaceSelect(place);
      } finally {
        setIsLoadingDetails(false);
      }
    } else if (onPlaceSelect) {
      onPlaceSelect(null);
    }
  };

  const handleInputChange = (e: any) => {
    setInput(e.target.value);
    if (!e.target.value) {
      if (onPlaceSelect) {
        onPlaceSelect(null);
      }
    }
  };

  // Dropdown item renderer
  const itemTemplate = (option: PlacePredictionItem) => {
    return <div className="place-item">{option.description}</div>;
  };

  return (
    <div
      className={`places-search-container ${className}${
        hasSearchInput ? "" : " places-search-empty"
      }`}
    >
      <span className="p-input-icon-right w-full">
        {(isLoading || isLoadingDetails) && (
          <i className="pi pi-spin pi-spinner" />
        )}
        <AutoComplete
          value={input}
          suggestions={autoCompleteOptions}
          completeMethod={() => {}} // No need to do anything here, useAutocompleteSuggestions already handles it
          field="description"
          onChange={handleInputChange}
          onSelect={(e) => handlePlaceSelect(e.value)}
          placeholder={placeholder}
          itemTemplate={itemTemplate}
          dropdown
          dropdownMode="current"
          className="w-full"
          forceSelection={false}
          delay={debounceTime}
        />
      </span>
    </div>
  );
};
